import { LicenseInfo } from "@mui/x-license";
import { HomeLayout } from "./features/home/HomeLayout";
import { LandingPage } from "./features/landing/LandingPage";
import { LoginPage } from "./features/login/LoginPage";
import { TalentProfile } from "./features/talentProfile/TalentProfile";
import { TalentsPage } from "./features/talents/TalentsPage";

import { CssBaseline, ThemeProvider } from "@mui/material";
import { Amplify } from "aws-amplify";
import { useEffect } from "react";
import {
  createBrowserRouter,
  RouteObject,
  RouterProvider,
} from "react-router-dom";
import "./App.css";
import { LocalizationProviderWrapper } from "./LocalizationProviderWrapper";
import { awsconfig } from "./aws-exports";
import { ErrorLayout } from "./common/components/functional/errorLayout/ErrorLayout";
import { CustomersPage } from "./features/customer/CustomersPage";
import CustomerManagement from "./features/customer/customerManagement/CustomerManagement";
import Dashboard from "./features/dashboard/Dashboard";
import EntriesPage from "./features/entries/EntriesPage";
import EditNewEntryModal from "./features/entries/Entry/EditEntry/EditNewEntryModal";
import { InvoicesPage } from "./features/invoices/InvoicesPage";
import { EditInvoicePage } from "./features/invoices/editInvoice/EditInvoicePage";
import { NewInvoicePage } from "./features/invoices/newInvoice/NewInvoicePage";
import { PreviewInvoicePage } from "./features/invoices/previewInvoice/PreviewInvoicePage";
import { ExamineInvoicePage } from "./features/invoices/examineInvoice/ExamineInvoicePage";
import { ExamineInvoiceLayout } from "./features/invoices/examineInvoice/ExamineInvoiceLayout";
import JobAdsPage from "./features/jobAds/JobAdsPage";
import LegacyLoginPage from "./features/login/LegacyLoginPage";
import { MaintenancePage } from "./features/maintenance/MaintenancePage";
import { OrdersPage } from "./features/orders/OrdersPage";
import { EditOrderPage } from "./features/orders/editOrder/EditOrderPage";
import { WorkAgreement } from "./features/orders/editOrder/long/workAgreement/WorkAgreement";
import { NewOrderPage } from "./features/orders/newOrder/NewOrderPage";
import { SalariesPage } from "./features/salaries/SalariesPage";
import { AdminSettings } from "./features/settings/AdminSettings";
import { AccessRightProvider } from "./features/user/AccessRightProvider";
import theme from "./theme";
import JobAdForm from "./features/jobAds/jobAdForm/JobAdView";
import EmploymentAdditionalInfo from "./features/talentProfile/Employment/EmploymentAdditionalInfo/EmploymentAdditionalInfo";
import AssignmentAdditionalInfo from "./features/talentProfile/Employment/AssignmentAdditionalInfo/AssignmentAdditionalInfo";
import FeedbacksPage from "./features/feedbacks/FeedbacksPage";
import MessagingPage from "./features/messaging/MessagingPage";
import { CopyLongOrder } from "./features/orders/copyOrder/CopyLongOrder";
import { ConfirmationDialogProvider } from "./contexts/ConfirmationDialogContext";
import { withCanonicalPathGuard } from "./app/CanonicalPathGuard";

Amplify.configure(awsconfig);
LicenseInfo.setLicenseKey(process.env.REACT_APP_CORE_MUI_LICENSE_KEY!!);
const useLegacyLogin = process.env.REACT_APP_USE_LEGACY_LOGIN;

const appRoutes: RouteObject[] = [
  { path: "/", element: <LandingPage /> },
  {
    path: "/login",
    element: useLegacyLogin ? <LegacyLoginPage /> : <LoginPage />,
  },
  {
    path: "/maintenance",
    element: <MaintenancePage />,
  },
  {
    path: "/home/invoices/examine/:invoiceId",
    element: <ExamineInvoiceLayout />,
    children: [
      { index: true, element: <ExamineInvoicePage /> },
      { path: "row/:rowId", element: <ExamineInvoicePage /> },
    ],
  },
  {
    path: "/home/",
    element: <HomeLayout />,
    errorElement: <ErrorLayout />,
    children: [
      { index: true, element: <Dashboard /> },
      {
        path: "messaging/",
        children: [{ index: true, element: <MessagingPage /> }],
      },
      {
        path: "entries/",
        children: [
          { index: true, element: <EntriesPage isReadOnly={false} /> },
        ],
      },
      {
        path: "talents/",
        children: [
          {
            index: true,
            element: <TalentsPage />,
          },
          {
            path: ":talentId/:tab",
            element: <TalentProfile />,
            children: [
              {
                path: ":entryId",
                element: <EditNewEntryModal />,
              },
            ],
          },
          {
            path: ":talentId/employment/:employmentId/",
            element: <EmploymentAdditionalInfo />,
          },
          {
            path: ":talentId/assignment/:assignmentId",
            element: <AssignmentAdditionalInfo />,
          },
        ],
      },
      {
        path: "customers/",
        children: [
          { index: true, element: <CustomersPage /> },
          {
            path: ":customerCompanyId/:tab",
            element: <CustomerManagement />,
          },
        ],
      },
      {
        path: "invoices/",
        children: [
          { index: true, element: <InvoicesPage /> },
          {
            path: "new/",
            children: [{ index: true, element: <NewInvoicePage /> }],
          },
          {
            path: "edit/",
            children: [{ path: ":invoiceIds", element: <EditInvoicePage /> }],
          },
          {
            path: "preview/",
            children: [
              { path: ":invoiceIds", element: <PreviewInvoicePage /> },
            ],
          },
        ],
      },
      {
        path: "orders/",
        children: [
          { index: true, element: <OrdersPage /> },
          {
            path: "new/",
            children: [{ index: true, element: <NewOrderPage /> }],
          },
          {
            path: ":bundleId/",
            children: [
              { index: true, element: <EditOrderPage /> },
              {
                path: ":workAgreementId/edit",
                children: [{ index: true, element: <WorkAgreement /> }],
              },
              {
                path: "offer",
                children: [{ index: true, element: <WorkAgreement /> }],
              },
              {
                path: "copy",
                children: [{ index: true, element: <CopyLongOrder /> }],
              },
            ],
          },
        ],
      },
      {
        path: "salaries/",
        children: [{ index: true, element: <SalariesPage /> }],
      },
      {
        path: "feedbacks/",
        children: [{ index: true, element: <FeedbacksPage /> }],
      },
      {
        path: "settings/:tab",
        children: [{ index: true, element: <AdminSettings /> }],
      },
      {
        path: "jobAds/",
        children: [
          { index: true, element: <JobAdsPage /> },
          {
            path: "new/",
            children: [{ index: true, element: <JobAdForm /> }],
          },
          {
            path: ":jobAdId",
            element: <JobAdForm />,
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(withCanonicalPathGuard(appRoutes));

function App() {
  // This useEffect will listen to logout events from other tabs (localStorage isLoggedIn) and redirect to login page
  useEffect(() => {
    const syncLogout = (event: any) => {
      if (event.key === "isLoggedIn" && event.newValue === "false") {
        // Redirect to login page
        window.location.href = "/login";
      }
    };

    window.addEventListener("storage", syncLogout);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("storage", syncLogout);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProviderWrapper>
        <AccessRightProvider>
          <ConfirmationDialogProvider>
            <RouterProvider router={router} />
          </ConfirmationDialogProvider>
        </AccessRightProvider>
      </LocalizationProviderWrapper>
    </ThemeProvider>
  );
}

export default App;
